import {
	Box,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
	Container,
	Paper,
	Alert,
} from '@mui/material';

import { lazy, Suspense, memo } from 'react';
import { KPICard } from './components/KPICard';

const FacultyStatsChart = lazy(() => import('./components/FacultyStatsChart').then(m => ({ default: m.FacultyStatsChart })));
const AdmissionMethodsPieChart = lazy(() => import('./components/AdmissionMethodsPieChart').then(m => ({ default: m.AdmissionMethodsPieChart })));
const Top10ProvincesBeforeMergeChart = lazy(() => import('./components/Top10ProvincesBeforeMergeChart').then(m => ({ default: m.Top10ProvincesBeforeMergeChart })));
const Top10ProvincesAfterMergeChart = lazy(() => import('./components/Top10ProvincesAfterMergeChart').then(m => ({ default: m.Top10ProvincesAfterMergeChart })));
const Grade12ScoreDistributionChart = lazy(() => import('./components/Grade12ScoreDistributionChart').then(m => ({ default: m.Grade12ScoreDistributionChart })));
const GenderDistributionChart = lazy(() => import('./components/GenderDistributionChart').then(m => ({ default: m.GenderDistributionChart })));
const TopSchoolsChart = lazy(() => import('./components/TopSchoolsChart').then(m => ({ default: m.TopSchoolsChart })));
const ProvinceByFacultyHeatmap = lazy(() => import('./components/ProvinceByFacultyHeatmap').then(m => ({ default: m.ProvinceByFacultyHeatmap })));
const ProvinceByMajorHeatmap = lazy(() => import('./components/ProvinceByMajorHeatmap').then(m => ({ default: m.ProvinceByMajorHeatmap })));
const RemainingQuotaChart = lazy(() => import('./components/RemainingQuotaChart').then(m => ({ default: m.RemainingQuotaChart })));
const TopSchoolsOverallChart = lazy(() => import('./components/TopSchoolsOverallChart').then(m => ({ default: m.TopSchoolsOverallChart })));

import { z } from 'zod';
import {
	dashboardApi,
	DashboardKPIsSchema,
	FacultyMajorStatsSchema,
	MajorStatsSchema,
	AdmissionMethodStatsSchema,
	GeographicStatsSchema,
	GeographicStatsMergedSchema,
	Grade12ScoreAnalysisSchema,
	GenderDistributionByFacultySchema,
	GenderDistributionByMajorSchema,
	TopSchoolsByMethodSchema,
	ProvinceByFacultyHeatmapSchema,
	ProvinceByMajorHeatmapSchema,
	RemainingQuotaStatsSchema
} from '../../services/dashboardApi';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAllFilters } from '../../store/slices/filtersSlice';

import { useProgressiveRender } from '../../hooks/useProgressiveRender';
import { safePercentage } from '../../utils/safePercentage';
import { DashboardSkeleton, KPISkeleton } from './components/DashboardSkeleton';
import { ChartYearProvider } from '../../contexts/chart-year-context';


type DashboardKPIs = z.infer<typeof DashboardKPIsSchema>;
type FacultyMajorStats = z.infer<typeof FacultyMajorStatsSchema>;
type MajorStats = z.infer<typeof MajorStatsSchema>;
type AdmissionMethodStats = z.infer<typeof AdmissionMethodStatsSchema>;
type GeographicStats = z.infer<typeof GeographicStatsSchema>;
type GeographicStatsMerged = z.infer<typeof GeographicStatsMergedSchema>;
type Grade12ScoreAnalysis = z.infer<typeof Grade12ScoreAnalysisSchema>;
type GenderDistributionByFaculty = z.infer<typeof GenderDistributionByFacultySchema>;
type GenderDistributionByMajor = z.infer<typeof GenderDistributionByMajorSchema>;
type TopSchoolsByMethod = z.infer<typeof TopSchoolsByMethodSchema>;
type ProvinceByFacultyHeatmap = z.infer<typeof ProvinceByFacultyHeatmapSchema>;
type ProvinceByMajorHeatmap = z.infer<typeof ProvinceByMajorHeatmapSchema>;
type RemainingQuotaStats = z.infer<typeof RemainingQuotaStatsSchema>;
type SchoolStats = z.infer<typeof TopSchoolsByMethodSchema>['schools'];



const ProgressiveSection = memo(({ children, index, show }: { children: React.ReactNode; index: number; show: boolean }) => {
	const shouldRender = useProgressiveRender(index, { enabled: show });

	if (!shouldRender) {
		return <DashboardSkeleton variant="chart" />;
	}

	return <>{children}</>;
});

ProgressiveSection.displayName = 'ProgressiveSection';

const DashboardPage: React.FC = () => {
	const dispatch = useAppDispatch();
	const filtersState = useAppSelector((state) => state.filters);
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const [loading, setLoading] = useState(true);
	const [hasNoData, setHasNoData] = useState(false);
	const [showProgressive, setShowProgressive] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
	const [facultyStats, setFacultyStats] = useState<FacultyMajorStats[]>([]);
	const [majorStats, setMajorStats] = useState<MajorStats[]>([]);
	const [admissionMethods, setAdmissionMethods] = useState<AdmissionMethodStats[]>([]);
	const [geographicStats, setGeographicStats] = useState<GeographicStats | null>(null);
	const [geographicStatsMerged, setGeographicStatsMerged] = useState<GeographicStatsMerged | null>(null);
	const [grade12Analysis, setGrade12Analysis] = useState<Grade12ScoreAnalysis | null>(null);
	const [genderDistribution, setGenderDistribution] = useState<GenderDistributionByFaculty[]>([]);
	const [genderDistributionByMajor, setGenderDistributionByMajor] = useState<GenderDistributionByMajor[]>([]);
	const [topSchools, setTopSchools] = useState<TopSchoolsByMethod[]>([]);
	const [heatmapFacultyBefore, setHeatmapFacultyBefore] = useState<ProvinceByFacultyHeatmap | null>(null);
	const [heatmapFacultyAfter, setHeatmapFacultyAfter] = useState<ProvinceByFacultyHeatmap | null>(null);
	const [heatmapMajorBefore, setHeatmapMajorBefore] = useState<ProvinceByMajorHeatmap | null>(null);
	const [heatmapMajorAfter, setHeatmapMajorAfter] = useState<ProvinceByMajorHeatmap | null>(null);
	const [remainingQuota, setRemainingQuota] = useState<RemainingQuotaStats[]>([]);
	const [topSchoolsOverall, setTopSchoolsOverall] = useState<SchoolStats>([]);
	const [topSchoolsTop149, setTopSchoolsTop149] = useState<SchoolStats>([]);

	const [selectedYear, setSelectedYear] = useState<number | null>(null);
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		if (!filtersState.initialized) {
			dispatch(fetchAllFilters());
		}
	}, [dispatch, filtersState.initialized]);

	useEffect(() => {
		if (filtersState.initialized && filtersState.years.length > 0 && !isInitialized) {
			const latestYear = Math.max(...filtersState.years);
			setSelectedYear(latestYear);
			setIsInitialized(true);
		} else if (filtersState.initialized && filtersState.years.length === 0 && !isInitialized) {
			setHasNoData(true);
			setLoading(false);
		}
	}, [filtersState.initialized, filtersState.years, isInitialized]);

	const loadDashboardData = useCallback(async () => {
		if (!isInitialized) return;

		try {
			setLoading(true);
			setError(null);

			const yearFilter = selectedYear;
			const maKhoaFilter = selectedFaculty;

			if (maKhoaFilter) {
				const [kpisData, majorStatsData, admissionData, geographicData, geographicMergedData, grade12Data, topSchoolsData, genderByMajorData, heatmapMajorBeforeData, heatmapMajorAfterData, remainingQuotaData, topSchoolsOverallData, topSchoolsTop149Data] =
					await Promise.all([
						dashboardApi.getKPIs(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getMajorStatsByFaculty(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getAdmissionMethodStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGeographicStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGeographicStatsMerged(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGrade12ScoreAnalysis(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchoolsByMethod(yearFilter || undefined, undefined, maKhoaFilter || undefined),
						dashboardApi.getGenderDistributionByMajor(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getProvinceByMajorHeatmap(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getProvinceByMajorHeatmapMerged(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getRemainingQuotaStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchools(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchoolsTop149(yearFilter || undefined, maKhoaFilter || undefined),
					]);

				setKpis(kpisData);
				setMajorStats(majorStatsData.sort((a, b) => a.majorCode.localeCompare(b.majorCode)));
				setFacultyStats([]);
				setAdmissionMethods(admissionData);
				setGeographicStats(geographicData);
				setGeographicStatsMerged(geographicMergedData);
				setGrade12Analysis(grade12Data);
				setTopSchools(topSchoolsData);
				setGenderDistributionByMajor(genderByMajorData.sort((a, b) => a.majorCode.localeCompare(b.majorCode)));
				setGenderDistribution([]);
				setHeatmapMajorBefore(heatmapMajorBeforeData);
				setHeatmapMajorAfter(heatmapMajorAfterData);
				setHeatmapFacultyBefore(null);
				setHeatmapFacultyAfter(null);
				setRemainingQuota(remainingQuotaData.sort((a, b) => a.code.localeCompare(b.code)));
				setTopSchoolsOverall(topSchoolsOverallData);
				setTopSchoolsTop149(topSchoolsTop149Data);
			} else {
				const [kpisData, facultyData, admissionData, geographicData, geographicMergedData, grade12Data, topSchoolsData, genderByFacultyData, heatmapFacultyBeforeData, heatmapFacultyAfterData, remainingQuotaData, topSchoolsOverallData, topSchoolsTop149Data] =
					await Promise.all([
						dashboardApi.getKPIs(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getFacultyMajorStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getAdmissionMethodStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGeographicStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGeographicStatsMerged(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getGrade12ScoreAnalysis(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchoolsByMethod(yearFilter || undefined, undefined, maKhoaFilter || undefined),
						dashboardApi.getGenderDistributionByFaculty(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getProvinceByFacultyHeatmap(yearFilter || undefined),
						dashboardApi.getProvinceByFacultyHeatmapMerged(yearFilter || undefined),
						dashboardApi.getRemainingQuotaStats(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchools(yearFilter || undefined, maKhoaFilter || undefined),
						dashboardApi.getTopSchoolsTop149(yearFilter || undefined, maKhoaFilter || undefined),
					]);

				setKpis(kpisData);
				setFacultyStats(facultyData.sort((a, b) => a.facultyCode.localeCompare(b.facultyCode)));
				setMajorStats([]);
				setAdmissionMethods(admissionData);
				setGeographicStats(geographicData);
				setGeographicStatsMerged(geographicMergedData);
				setGrade12Analysis(grade12Data);
				setTopSchools(topSchoolsData);
				setGenderDistribution(genderByFacultyData.sort((a, b) => a.facultyCode.localeCompare(b.facultyCode)));
				setGenderDistributionByMajor([]);
				setHeatmapFacultyBefore(heatmapFacultyBeforeData);
				setHeatmapFacultyAfter(heatmapFacultyAfterData);
				setHeatmapMajorBefore(null);
				setHeatmapMajorAfter(null);
				setRemainingQuota(remainingQuotaData.sort((a, b) => a.code.localeCompare(b.code)));
				setTopSchoolsOverall(topSchoolsOverallData);
				setTopSchoolsTop149(topSchoolsTop149Data);
			}
			setShowProgressive(true);
		} catch (err) {
			console.error('Error loading dashboard data:', err);
			setError('Không thể tải dữ liệu dashboard. Vui lòng thử lại.');
		} finally {
			setLoading(false);
		}
	}, [selectedYear, selectedFaculty, isInitialized]);

	useEffect(() => {
		loadDashboardData();
	}, [loadDashboardData]);

	const handleYearChange = (value: string) => {
		if (value === '') {
			setSelectedYear(null);
		} else {
			setSelectedYear(Number(value));
		}
	};

	// Hoisted so FacultyStatsChart (wrapped in memo()) receives a stable array
	// reference across re-renders that don't change the underlying stats.
	const facultyStatsChartData = useMemo(() => (
		selectedFaculty && majorStats.length > 0
			? majorStats.map(m => ({
				facultyCode: m.majorCode,
				facultyName: m.majorName,
				studentCount: m.studentCount
			}))
			: facultyStats.map(f => ({
				facultyCode: f.facultyCode,
				facultyName: f.facultyName,
				studentCount: f.studentCount
			}))
	), [selectedFaculty, majorStats, facultyStats]);

	if (hasNoData) {
		return (
			<Container maxWidth="xl" sx={{ py: 4 }}>
				<Alert severity="info">
					Chưa có dữ liệu tuyển sinh. Vui lòng nhập dữ liệu để xem thống kê.
				</Alert>
			</Container>
		);
	}

	if (loading) {
		return (
			<Container maxWidth="xl" sx={{ py: 4 }}>
				<Box display="flex" justifyContent="center">
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	if (error) {
		return (
			<Container maxWidth="xl" sx={{ py: 4 }}>
				<Alert severity="error">{error}</Alert>
			</Container>
		);
	}

	return (
		<ChartYearProvider year={selectedYear}>
			<Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
				<Typography
					variant="h3"
					component="h1"
					gutterBottom
					fontWeight="bold"
					sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
				>
					Bảng điều hành
				</Typography>

				<Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
						<FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
							<InputLabel>Năm học</InputLabel>
							<Select
								value={selectedYear?.toString() || ''}
								onChange={(e) => handleYearChange(e.target.value)}
								label="Năm học"
							>
								{filtersState.years?.map((year) => (
									<MenuItem key={year} value={year.toString()}>
										{year}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				</Paper>

				{loading && <KPISkeleton />}

				{!loading && kpis && (
					<Box sx={{
						display: 'grid',
						gridTemplateColumns: {
							xs: '1fr',
							sm: 'repeat(2, 1fr)',
							md: 'repeat(3, 1fr)'
						},
						gap: { xs: 2, sm: 2, md: 3 },
						mb: { xs: 3, md: 4 }
					}}>
						<KPICard
							title="Tổng chỉ tiêu"
							value={kpis.totalQuota}
							color="primary"
						/>
						<KPICard
							title="Số lượng thí sinh nhập học"
							value={kpis.enrollmentCount}
							subtitle={`${safePercentage(kpis.enrollmentPercentage).toFixed(2)}% chỉ tiêu tuyển sinh`}
							color="success"
						/>
						<KPICard
							title="Sinh viên từ top 149 trường"
							value={kpis.top149EnrollmentCount}
							subtitle={`${safePercentage(kpis.top149Percentage).toFixed(2)}% tổng sinh viên nhập học`}
							color="warning"
						/>
						<KPICard
							title="Tổng sinh viên"
							value={kpis.totalStudents}
							color="secondary"
						/>
						<KPICard
							title="Tỉnh thành"
							value={kpis.totalProvinces}
							color="secondary"
						/>
						<KPICard
							title="Sinh viên có điểm Lớp 12"
							value={kpis.studentsWithGrade12Score}
							color="success"
						/>
					</Box>
				)}

				<ProgressiveSection index={0} show={showProgressive && !loading}>
					<Box sx={{ mb: { xs: 3, md: 4 } }}>
						<Suspense fallback={<DashboardSkeleton variant="chart" />}>
							<FacultyStatsChart
								data={facultyStatsChartData}
								title={selectedFaculty ? "Thống kê theo Ngành" : "Thống kê theo Khoa"}
							/>
						</Suspense>
					</Box>
				</ProgressiveSection>

				<ProgressiveSection index={1} show={showProgressive && !loading}>
					{remainingQuota.length > 0 && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" />}>
								<RemainingQuotaChart
									data={remainingQuota}
									title={selectedFaculty ? "Chỉ tiêu còn thiếu theo Ngành" : "Chỉ tiêu còn thiếu theo Khoa"}
								/>
							</Suspense>
						</Box>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={2} show={showProgressive && !loading}>
					<Box sx={{ mb: { xs: 3, md: 4 } }}>
						<Suspense fallback={<DashboardSkeleton variant="pie" height={400} />}>
							<AdmissionMethodsPieChart admissionMethods={admissionMethods} />
						</Suspense>
					</Box>
				</ProgressiveSection>

				<ProgressiveSection index={3} show={showProgressive && !loading}>
					<Box sx={{ mb: { xs: 3, md: 4 } }}>
						<Suspense fallback={<DashboardSkeleton variant="chart" />}>
							<Top10ProvincesBeforeMergeChart data={geographicStats?.top10Provinces ?? []} />
						</Suspense>
					</Box>
					<Box sx={{ mb: { xs: 3, md: 4 } }}>
						<Suspense fallback={<DashboardSkeleton variant="chart" />}>
							<Top10ProvincesAfterMergeChart data={geographicStatsMerged?.top10ProvincesMerged ?? []} />
						</Suspense>
					</Box>
				</ProgressiveSection>

				<ProgressiveSection index={4} show={showProgressive && !loading}>
					{topSchoolsOverall.length > 0 && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" height={500} />}>
								<TopSchoolsOverallChart
									data={topSchoolsOverall}
									title="Top 10 trường THPT được tuyển sinh nhiều nhất"
								/>
							</Suspense>
						</Box>
					)}
					{topSchoolsTop149.length > 0 && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" height={500} />}>
								<TopSchoolsOverallChart
									data={topSchoolsTop149}
									title="Top 10 trường thuộc top149 được tuyển sinh nhiều nhất"
								/>
							</Suspense>
						</Box>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={5} show={showProgressive && !loading}>
					{!selectedFaculty && heatmapFacultyBefore && heatmapFacultyAfter && (
						<>
							<Box sx={{ mb: { xs: 3, md: 4 } }}>
								<Suspense fallback={<DashboardSkeleton variant="chart" height={600} />}>
									<ProvinceByFacultyHeatmap
										data={heatmapFacultyBefore.data}
										title="Heatmap Tỉnh/Thành x Khoa (Trước sáp nhập)"
										height={600}
									/>
								</Suspense>
							</Box>
							<Box sx={{ mb: { xs: 3, md: 4 } }}>
								<Suspense fallback={<DashboardSkeleton variant="chart" height={600} />}>
									<ProvinceByFacultyHeatmap
										data={heatmapFacultyAfter.data}
										title="Heatmap Tỉnh/Thành x Khoa (Sau sáp nhập)"
										height={600}
									/>
								</Suspense>
							</Box>
						</>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={6} show={showProgressive && !loading}>
					{selectedFaculty && heatmapMajorBefore && heatmapMajorAfter && (
						<>
							<Box sx={{ mb: { xs: 3, md: 4 } }}>
								<Suspense fallback={<DashboardSkeleton variant="chart" height={600} />}>
									<ProvinceByMajorHeatmap
										data={heatmapMajorBefore.data}
										title="Heatmap Tỉnh/Thành x Ngành (Trước sáp nhập)"
										height={800}
									/>
								</Suspense>
							</Box>
							<Box sx={{ mb: { xs: 3, md: 4 } }}>
								<Suspense fallback={<DashboardSkeleton variant="chart" height={600} />}>
									<ProvinceByMajorHeatmap
										data={heatmapMajorAfter.data}
										title="Heatmap Tỉnh/Thành x Ngành (Sau sáp nhập)"
										height={800}
									/>
								</Suspense>
							</Box>
						</>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={7} show={showProgressive && !loading}>
					{grade12Analysis && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" />}>
								<Grade12ScoreDistributionChart scoreDistribution={grade12Analysis?.scoreDistribution} />
							</Suspense>
						</Box>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={8} show={showProgressive && !loading}>
					{(genderDistribution.length > 0 || genderDistributionByMajor.length > 0) && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" />}>
								<GenderDistributionChart
									data={selectedFaculty ? genderDistributionByMajor : genderDistribution}
									title={selectedFaculty ? "Phân bố giới tính theo Ngành" : "Phân bố giới tính theo Khoa"}
									chartType="bar"
								/>
							</Suspense>
						</Box>
					)}
				</ProgressiveSection>

				<ProgressiveSection index={9} show={showProgressive && !loading}>
					{topSchools.length > 0 && (
						<Box sx={{ mb: { xs: 3, md: 4 } }}>
							<Suspense fallback={<DashboardSkeleton variant="chart" height={500} />}>
								<TopSchoolsChart
									data={topSchools}
									title="Top 10 trường THPT theo phương thức tuyển sinh"
								/>
							</Suspense>
						</Box>
					)}
				</ProgressiveSection>

			</Container>
		</ChartYearProvider>
	);
};

export default DashboardPage;
